import { useState, useEffect, useCallback } from 'react';
import api from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Toaster, toast } from 'sonner';
import { 
  MonitorPlay, Monitor, PaintBrush, Image, 
  Megaphone, Eye, ArrowsClockwise, Copy, Trash,
  Check, X, Upload
} from '@phosphor-icons/react';

const TIME_FORMATS = [
  { value: '24h', label: '24 Hour (14:30)' },
  { value: '12h', label: '12 Hour (2:30 PM)' }
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: '07/04/2026' },
  { value: 'D MMMM YYYY', label: '7 April 2026' },
  { value: 'dddd D MMMM YYYY', label: 'Tuesday, 7 April 2026' }
];

const CLOCK_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'Extra Large' }
];

const CLOCK_STYLES = [
  { value: 'modern', label: 'Modern (Bold)' },
  { value: 'digital', label: 'Digital (LED)' },
  { value: 'minimal', label: 'Minimal (Thin)' }
];

const FONT_FAMILIES = [
  { value: 'Anton', label: 'Anton' },
  { value: 'Bebas Neue', label: 'Bebas Neue' },
  { value: 'Oswald', label: 'Oswald' },
  { value: 'Roboto', label: 'Roboto' }
];

const LOGO_POSITIONS = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'hidden', label: 'Hidden' }
];

const LOGO_SIZES = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' }
];

const EFFECT_STYLES = [
  { value: 'glow_pulse', label: 'Glow Pulse' },
  { value: 'stadium_flash', label: 'Stadium Flash' },
  { value: 'cinematic_sweep', label: 'Cinematic Sweep' },
  { value: 'energetic_burst', label: 'Energetic Burst' }
];

const MESSAGE_ANIMATIONS = [
  { value: 'static', label: 'Static (Fixed)' },
  { value: 'scroll', label: 'Scrolling (Right to Left)' },
  { value: 'blink', label: 'Blinking' }
];

export default function AdminDashboard() {
  const [screens, setScreens] = useState([]);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [editingScreen, setEditingScreen] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchScreens = useCallback(async () => {
    try {
      const data = await api.getScreens();
      setScreens(data);
    } catch (err) {
      console.error('Failed to fetch screens:', err);
      toast.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScreens();
    // Listen for real-time Firebase updates
    const unsubscribe = api.onAllScreensChange((data) => {
      if (data && data.length === 6) {
        setScreens(data);
      }
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [fetchScreens]);

  const handleScreenSelect = (screenNumber) => {
    if (isBulkMode) {
      setSelectedScreens(prev => 
        prev.includes(screenNumber) 
          ? prev.filter(n => n !== screenNumber)
          : [...prev, screenNumber]
      );
    } else {
      const screen = screens.find(s => s.screen_number === screenNumber);
      setEditingScreen(screenNumber);
      setEditForm({ ...screen });
    }
  };

  const selectAllScreens = () => {
    setSelectedScreens(screens.map(s => s.screen_number));
  };

  const deselectAllScreens = () => {
    setSelectedScreens([]);
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isBulkMode && selectedScreens.length > 0) {
        await api.bulkUpdateScreens(selectedScreens, editForm);
        toast.success(`${selectedScreens.length} screen(s) updated!`);
      } else if (editingScreen) {
        await api.updateScreen(editingScreen, editForm);
        toast.success('Screen updated!');
      }
      await fetchScreens();
      if (editingScreen) {
        const updatedScreen = await api.getScreen(editingScreen);
        setEditForm(updatedScreen);
      }
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (screenNumber) => {
    try {
      await api.resetScreen(screenNumber);
      await fetchScreens();
      if (editingScreen === screenNumber) {
        const screen = await api.getScreen(screenNumber);
        setEditForm({ ...screen });
      }
      toast.success('Screen reset to defaults!');
    } catch (err) {
      console.error('Failed to reset:', err);
      toast.error('Failed to reset screen');
    }
  };

  const handleCopyFrom = async (targetScreen, sourceScreen) => {
    try {
      await api.copyScreenConfig(targetScreen, sourceScreen);
      await fetchScreens();
      toast.success(`Configuration copied from Screen ${sourceScreen}!`);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy configuration');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);

    try {
      const data = await api.uploadLogo(file);
      handleFormChange('logo_url', data.url);
      toast.success('Logo uploaded!');
    } catch (err) {
      console.error('Failed to upload logo:', err);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white text-xl font-['Manrope'] animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-['Manrope']" data-testid="admin-dashboard">
      <Toaster position="top-right" theme="dark" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#27272A]/50 bg-[#0A0A0A] py-4 px-8 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <MonitorPlay size={32} weight="bold" className="text-[#007AFF]" />
          <span className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
            MVP LAB
          </span>
          <span className="text-[#A1A1AA] text-sm ml-2">Screen Control</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
          onClick={async () => {
            await api.triggerRefresh();
            toast.success('Refresh signal sent to all screens');
          }}
          data-testid="refresh-screens-btn"
        >
          <ArrowsClockwise size={16} className="mr-2" />
          Refresh Screens
        </Button>
      </header>

      <main className="p-8">
        {/* Mode Toggle */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Screen Management</h1>
            <p className="text-[#A1A1AA]">Configure and manage your 6 display screens</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-[#121212] border border-[#27272A] px-4 py-2">
              <span className={`text-sm ${!isBulkMode ? 'text-white' : 'text-[#52525B]'}`}>Single Edit</span>
              <Switch
                checked={isBulkMode}
                onCheckedChange={(checked) => {
                  setIsBulkMode(checked);
                  if (!checked) {
                    setSelectedScreens([]);
                    setEditForm({});
                  } else {
                    setEditingScreen(null);
                    setEditForm({});
                  }
                }}
                data-testid="bulk-mode-toggle"
              />
              <span className={`text-sm ${isBulkMode ? 'text-white' : 'text-[#52525B]'}`}>Bulk Edit</span>
            </div>
            
            {isBulkMode && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllScreens}
                  className="border-[#27272A] text-white hover:bg-[#27272A]"
                  data-testid="select-all-btn"
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllScreens}
                  className="border-[#27272A] text-white hover:bg-[#27272A]"
                  data-testid="deselect-all-btn"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Screen Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {screens.map((screen) => (
            <div
              key={screen.screen_number}
              onClick={() => handleScreenSelect(screen.screen_number)}
              className={`
                border bg-[#121212] p-6 cursor-pointer transition-all
                ${isBulkMode && selectedScreens.includes(screen.screen_number) 
                  ? 'border-[#007AFF] ring-1 ring-[#007AFF]' 
                  : editingScreen === screen.screen_number 
                    ? 'border-[#007AFF]' 
                    : 'border-[#27272A] hover:border-white/20'}
              `}
              data-testid={`screen-card-${screen.screen_number}`}
            >
              {/* Mini Preview */}
              <div 
                className="aspect-video mb-4 flex items-center justify-center relative overflow-hidden"
                style={{ backgroundColor: screen.background_color }}
              >
                {screen.message_enabled && screen.message_text ? (
                  <div className="absolute inset-0 bg-black flex items-center justify-center p-2">
                    <p className="text-white text-xs font-bold uppercase truncate">{screen.message_text}</p>
                  </div>
                ) : (
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: screen.text_color, fontFamily: `'${screen.font_family}', sans-serif` }}
                  >
                    12:00
                  </span>
                )}
                {screen.logo_url && screen.logo_position !== 'hidden' && (
                  <img 
                    src={screen.logo_url}
                    alt="Logo"
                    className="absolute w-6 h-6 object-contain"
                    style={{
                      top: screen.logo_position.includes('top') ? '4px' : 'auto',
                      bottom: screen.logo_position.includes('bottom') ? '4px' : 'auto',
                      left: screen.logo_position.includes('left') ? '4px' : 'auto',
                      right: screen.logo_position.includes('right') ? '4px' : 'auto',
                    }}
                  />
                )}
                {isBulkMode && selectedScreens.includes(screen.screen_number) && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-[#007AFF] flex items-center justify-center">
                    <Check size={14} weight="bold" />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">
                    {screen.custom_name || `Screen ${screen.screen_number}`}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm">Screen #{screen.screen_number}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <a
                    href={`/screen/${screen.screen_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 text-[#A1A1AA] hover:text-white transition-colors"
                    data-testid={`view-screen-${screen.screen_number}`}
                  >
                    <Eye size={18} />
                  </a>
                </div>
              </div>

              {/* Status Indicators */}
              <div className="flex items-center gap-2 mt-3">
                {screen.message_enabled && (
                  <span className="bg-[#FF3B30]/20 text-[#FF3B30] text-xs px-2 py-1 uppercase tracking-wider">
                    Announcement
                  </span>
                )}
                {screen.effect_enabled && (
                  <span className="bg-[#007AFF]/20 text-[#007AFF] text-xs px-2 py-1 uppercase tracking-wider">
                    Effects
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Editor Panel */}
        {(editingScreen || (isBulkMode && selectedScreens.length > 0)) && (
          <div className="border border-[#27272A] bg-[#121212] p-6" data-testid="editor-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {isBulkMode 
                  ? `Editing ${selectedScreens.length} Screen${selectedScreens.length > 1 ? 's' : ''}`
                  : `Edit ${editForm.custom_name || `Screen ${editingScreen}`}`
                }
              </h2>
              
              <div className="flex items-center gap-2">
                <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-[#27272A] text-white hover:bg-[#27272A]" data-testid="preview-btn">
                      <Eye size={16} className="mr-2" />
                      Preview
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl bg-[#0A0A0A] border-[#27272A]">
                    <DialogHeader>
                      <DialogTitle className="text-white">Preview</DialogTitle>
                    </DialogHeader>
                    <div 
                      className="aspect-video flex items-center justify-center relative overflow-hidden"
                      style={{ backgroundColor: editForm.background_color || '#0A0A0A' }}
                    >
                      {editForm.message_enabled && editForm.message_text ? (
                        <div className="absolute inset-0 bg-black flex items-center justify-center p-8">
                          <p 
                            className="text-4xl font-bold uppercase text-center"
                            style={{ color: '#FFFFFF', fontFamily: `'${editForm.font_family || 'Anton'}', sans-serif` }}
                          >
                            {editForm.message_text}
                          </p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span 
                            className="text-8xl font-bold"
                            style={{ color: editForm.text_color || '#FFFFFF', fontFamily: `'${editForm.font_family || 'Anton'}', sans-serif` }}
                          >
                            12:00:00
                          </span>
                          {editForm.show_date && (
                            <p 
                              className="text-2xl mt-2"
                              style={{ color: editForm.text_color || '#FFFFFF' }}
                            >
                              07/04/2026
                            </p>
                          )}
                        </div>
                      )}
                      {editForm.logo_url && editForm.logo_position !== 'hidden' && (
                        <img 
                          src={editForm.logo_url}
                          alt="Logo"
                          className="absolute w-16 h-16 object-contain"
                          style={{
                            top: editForm.logo_position?.includes('top') ? '16px' : 'auto',
                            bottom: editForm.logo_position?.includes('bottom') ? '16px' : 'auto',
                            left: editForm.logo_position?.includes('left') ? '16px' : 'auto',
                            right: editForm.logo_position?.includes('right') ? '16px' : 'auto',
                          }}
                        />
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                
                {!isBulkMode && (
                  <>
                    <Button 
                      variant="outline" 
                      onClick={() => handleReset(editingScreen)}
                      className="border-[#27272A] text-white hover:bg-[#27272A]"
                      data-testid="reset-btn"
                    >
                      <ArrowsClockwise size={16} className="mr-2" />
                      Reset
                    </Button>
                    <Select onValueChange={(val) => handleCopyFrom(editingScreen, parseInt(val))}>
                      <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-[#27272A] text-white" data-testid="copy-from-select">
                        <Copy size={16} className="mr-2" />
                        <SelectValue placeholder="Copy from..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {screens.filter(s => s.screen_number !== editingScreen).map(s => (
                          <SelectItem key={s.screen_number} value={s.screen_number.toString()} className="text-white hover:bg-[#27272A]">
                            Screen {s.screen_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
                
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditingScreen(null);
                    setEditForm({});
                  }}
                  className="text-[#A1A1AA] hover:text-white"
                  data-testid="close-editor-btn"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>

            <Tabs defaultValue="display" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-[#0A0A0A] border border-[#27272A] p-1 h-auto">
                <TabsTrigger value="display" className="data-[state=active]:bg-[#27272A] text-white py-3" data-testid="tab-display">
                  <Monitor size={16} className="mr-2" />
                  Display
                </TabsTrigger>
                <TabsTrigger value="style" className="data-[state=active]:bg-[#27272A] text-white py-3" data-testid="tab-style">
                  <PaintBrush size={16} className="mr-2" />
                  Style
                </TabsTrigger>
                <TabsTrigger value="logo" className="data-[state=active]:bg-[#27272A] text-white py-3" data-testid="tab-logo">
                  <Image size={16} className="mr-2" />
                  Logo
                </TabsTrigger>
                <TabsTrigger value="message" className="data-[state=active]:bg-[#27272A] text-white py-3" data-testid="tab-message">
                  <Megaphone size={16} className="mr-2" />
                  Message
                </TabsTrigger>
              </TabsList>

              {/* Display Tab */}
              <TabsContent value="display" className="mt-6 space-y-6">
                {!isBulkMode && (
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Screen Name</Label>
                    <Input
                      value={editForm.custom_name || ''}
                      onChange={(e) => handleFormChange('custom_name', e.target.value)}
                      placeholder="Enter screen name"
                      className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#007AFF] rounded-none"
                      data-testid="screen-name-input"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Time Format</Label>
                    <Select value={editForm.time_format} onValueChange={(val) => handleFormChange('time_format', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="time-format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {TIME_FORMATS.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Clock Style</Label>
                    <Select value={editForm.clock_style || 'modern'} onValueChange={(val) => handleFormChange('clock_style', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="clock-style-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {CLOCK_STYLES.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Date Format</Label>
                    <Select value={editForm.date_format} onValueChange={(val) => handleFormChange('date_format', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="date-format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {DATE_FORMATS.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Clock Size</Label>
                    <Select value={editForm.clock_size} onValueChange={(val) => handleFormChange('clock_size', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="clock-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {CLOCK_SIZES.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-y border-[#27272A]">
                  <div>
                    <Label className="text-white">Show Date</Label>
                    <p className="text-[#A1A1AA] text-sm">Display date below the clock</p>
                  </div>
                  <Switch
                    checked={editForm.show_date}
                    onCheckedChange={(val) => handleFormChange('show_date', val)}
                    data-testid="show-date-toggle"
                  />
                </div>

                <div className="flex items-center justify-between py-4 border-b border-[#27272A]">
                  <div>
                    <Label className="text-white">Hourly Effects</Label>
                    <p className="text-[#A1A1AA] text-sm">Visual effects at the top of each hour</p>
                  </div>
                  <Switch
                    checked={editForm.effect_enabled}
                    onCheckedChange={(val) => handleFormChange('effect_enabled', val)}
                    data-testid="effects-toggle"
                  />
                </div>

                {editForm.effect_enabled && (
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Effect Style</Label>
                    <Select value={editForm.effect_style} onValueChange={(val) => handleFormChange('effect_style', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="effect-style-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {EFFECT_STYLES.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </TabsContent>

              {/* Style Tab */}
              <TabsContent value="style" className="mt-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Background Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={editForm.background_color || '#0A0A0A'}
                        onChange={(e) => handleFormChange('background_color', e.target.value)}
                        className="w-12 h-12 border-0 bg-transparent cursor-pointer"
                        data-testid="bg-color-picker"
                      />
                      <Input
                        value={editForm.background_color || '#0A0A0A'}
                        onChange={(e) => handleFormChange('background_color', e.target.value)}
                        className="bg-[#0A0A0A] border-[#27272A] text-white font-mono rounded-none"
                        data-testid="bg-color-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Text Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={editForm.text_color || '#FFFFFF'}
                        onChange={(e) => handleFormChange('text_color', e.target.value)}
                        className="w-12 h-12 border-0 bg-transparent cursor-pointer"
                        data-testid="text-color-picker"
                      />
                      <Input
                        value={editForm.text_color || '#FFFFFF'}
                        onChange={(e) => handleFormChange('text_color', e.target.value)}
                        className="bg-[#0A0A0A] border-[#27272A] text-white font-mono rounded-none"
                        data-testid="text-color-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Font Family</Label>
                  <Select value={editForm.font_family} onValueChange={(val) => handleFormChange('font_family', val)}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="font-family-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {FONT_FAMILIES.map(f => (
                        <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              {/* Logo Tab */}
              <TabsContent value="logo" className="mt-6 space-y-6">
                <div className="space-y-4">
                  <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Upload Logo</Label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1">
                      <div className="bg-[#0A0A0A] border border-[#27272A] border-dashed p-8 text-center cursor-pointer hover:border-[#007AFF] transition-colors">
                        <Upload size={32} className="mx-auto mb-2 text-[#A1A1AA]" />
                        <p className="text-[#A1A1AA] text-sm">
                          {uploadingLogo ? 'Uploading...' : 'Click to upload logo'}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-upload-input"
                      />
                    </label>
                    
                    {editForm.logo_url && (
                      <div className="w-32 h-32 bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center p-2">
                        <img 
                          src={editForm.logo_url}
                          alt="Current logo"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                  
                  {editForm.logo_url && (
                    <Button
                      variant="outline"
                      onClick={() => handleFormChange('logo_url', null)}
                      className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                      data-testid="remove-logo-btn"
                    >
                      <Trash size={16} className="mr-2" />
                      Remove Logo
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Logo Position</Label>
                    <Select value={editForm.logo_position} onValueChange={(val) => handleFormChange('logo_position', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="logo-position-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {LOGO_POSITIONS.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Logo Size</Label>
                    <Select value={editForm.logo_size} onValueChange={(val) => handleFormChange('logo_size', val)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="logo-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#27272A]">
                        {LOGO_SIZES.map(f => (
                          <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>

              {/* Message Tab */}
              <TabsContent value="message" className="mt-6 space-y-6">
                <div className="flex items-center justify-between py-4 border-b border-[#27272A]">
                  <div>
                    <Label className="text-white">Enable Announcement</Label>
                    <p className="text-[#A1A1AA] text-sm">Display message instead of clock</p>
                  </div>
                  <Switch
                    checked={editForm.message_enabled}
                    onCheckedChange={(val) => handleFormChange('message_enabled', val)}
                    data-testid="message-toggle"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Message Text</Label>
                  <Textarea
                    value={editForm.message_text || ''}
                    onChange={(e) => handleFormChange('message_text', e.target.value)}
                    placeholder="Enter your announcement message..."
                    className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#007AFF] rounded-none min-h-[120px]"
                    data-testid="message-text-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Animation Style</Label>
                  <Select value={editForm.message_animation || 'static'} onValueChange={(val) => handleFormChange('message_animation', val)}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white rounded-none" data-testid="message-animation-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      {MESSAGE_ANIMATIONS.map(f => (
                        <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#27272A]">{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message Preview */}
                {editForm.message_text && (
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] uppercase tracking-wider text-xs">Preview</Label>
                    <div className="bg-black aspect-video flex items-center justify-center p-8">
                      <p className="text-white text-2xl font-bold uppercase text-center" style={{ fontFamily: `'${editForm.font_family || 'Anton'}', sans-serif` }}>
                        {editForm.message_text}
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Save Button */}
            <div className="flex justify-end mt-8 pt-6 border-t border-[#27272A]">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#007AFF] hover:bg-[#005BB5] text-white uppercase tracking-wider font-bold text-sm px-8 py-3 rounded-none"
                data-testid="save-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
